/** Shared cap-table math + API payload builder for network link-investment flows (dashboard + startup detail). */

export const TRANCHE_SLOT_MAX = 10;

export const LINK_STARTUP_OVERVIEW_TAB = { id: 'overview', label: 'Overview' };

const LINK_STARTUP_SECTION_COLUMNS = {
    overview: [
        { label: 'Gross investment committed', networkKey: 'networkAmount', stateKey: 'amounts', step: '1' },
        { label: 'Partly Paid Amount', networkKey: 'networkPartlyPaid', stateKey: 'partlyPaidAmounts', step: '0.01', derived: true, derivedTitle: 'Gross committed × partly paid %.' },
        { label: 'Nett Subscription Amount', networkKey: 'networkNettSubscription', stateKey: 'nettSubscriptionAmounts', step: '0.01', derived: true, derivedTitle: 'Gross committed − partly paid.' },
        { label: 'No. of CCPS', networkKey: 'networkShareCount', stateKey: 'shareCounts', step: '1' },
        { label: 'Post holding %', networkKey: 'networkEquityPercentage', stateKey: 'equityPercentages', step: '0.01' }
    ]
};

export function createEmptyTrancheSlots() {
    return Array.from({ length: TRANCHE_SLOT_MAX }, () => ({
        networkSub: '',
        networkCcps: '',
        networkHolding: '',
        invSub: {},
        invCcps: {},
        invHolding: {}
    }));
}

export function defaultTrancheSplitPcts() {
    return Array.from({ length: TRANCHE_SLOT_MAX }, (_, i) => (i < 2 ? '50' : '0'));
}

/** Build API payload for persisted tranche split / count on investor_network_startups. */
export function extractCapTableLinkSettingsFromForm(form) {
    const trancheCount = linkTrancheCount(form);
    const pcts = normalizeTrancheSplitPcts(form);
    return {
        trancheCount,
        trancheSplitPcts: pcts.slice(0, TRANCHE_SLOT_MAX)
    };
}

/** Merge saved settings from GET cap-table-link-settings into link form state. */
export function mergeCapTableLinkSettingsIntoForm(form, settings) {
    if (!settings || typeof settings !== 'object') return form;
    const next = { ...form };
    const tc = Number(settings.trancheCount);
    if (Number.isFinite(tc) && tc >= 1) {
        next.trancheCount = Math.min(TRANCHE_SLOT_MAX, Math.floor(tc));
    }
    if (Array.isArray(settings.trancheSplitPcts)) {
        const pcts = defaultTrancheSplitPcts();
        settings.trancheSplitPcts.forEach((v, i) => {
            if (i < TRANCHE_SLOT_MAX && v !== undefined && v !== null) {
                pcts[i] = String(v);
            }
        });
        next.trancheSplitPcts = pcts;
    }
    return next;
}

/**
 * Infer tranche split weights from saved per-investor subscription amounts (first row with data).
 */
export function inferTrancheSplitPctsFromInvestmentRows(rows, trancheCount) {
    const tc = Math.min(TRANCHE_SLOT_MAX, Math.max(1, trancheCount || 2));
    const list = Array.isArray(rows) ? rows : [];
    const subKeys = [
        'tranche1SubscriptionAmount',
        'tranche2SubscriptionAmount',
        'postCompletionSubscriptionAmount'
    ];
    let ref = null;
    for (const r of list) {
        const subs = subKeys.slice(0, tc).map((k) => parseLinkInvestmentNumber(r?.[k]));
        const sum = subs.reduce((a, b) => a + (Number.isFinite(b) && b > 0 ? b : 0), 0);
        if (sum > 0) {
            ref = subs;
            break;
        }
    }
    const pcts = defaultTrancheSplitPcts();
    if (!ref) return pcts;
    const total = ref.reduce((a, b) => a + (Number.isFinite(b) && b > 0 ? b : 0), 0);
    if (total <= 0) return pcts;
    for (let i = 0; i < tc; i++) {
        const v = ref[i];
        pcts[i] = Number.isFinite(v) && v > 0 ? String(Math.round((v / total) * 10000) / 100) : '0';
    }
    return pcts;
}

export function cloneTrancheSlotsFromForm(form) {
    const src = form.trancheSlots?.length === TRANCHE_SLOT_MAX ? form.trancheSlots : createEmptyTrancheSlots();
    return src.map((s) => ({
        networkSub: s.networkSub ?? '',
        networkCcps: s.networkCcps ?? '',
        networkHolding: s.networkHolding ?? '',
        invSub: { ...(s.invSub || {}) },
        invCcps: { ...(s.invCcps || {}) },
        invHolding: { ...(s.invHolding || {}) }
    }));
}

export function normalizeTrancheSplitPcts(form) {
    return Array.from({ length: TRANCHE_SLOT_MAX }, (_, i) =>
        form.trancheSplitPcts?.[i] !== undefined && form.trancheSplitPcts[i] !== null ? String(form.trancheSplitPcts[i]) : '0'
    );
}

export function getTrancheTabColumns(slotIndex) {
    return [
        {
            key: `tr-${slotIndex}-sub`,
            label: 'Subscription Amount',
            trancheSlotIndex: slotIndex,
            tranchePart: 'Sub',
            step: '0.01',
            derivedTitle:
                'When tranche split % on Overview is active, amounts fill from that split; you can still edit here—editing clears the split % so your amounts are kept.'
        },
        {
            key: `tr-${slotIndex}-ccps`,
            label: 'No. of CCPS',
            trancheSlotIndex: slotIndex,
            tranchePart: 'Ccps',
            step: '1',
            derived: true,
            derivedTitle: 'Subscription amount ÷ issue price.'
        },
        {
            key: `tr-${slotIndex}-hold`,
            label: 'Holding Percentage',
            trancheSlotIndex: slotIndex,
            tranchePart: 'Holding',
            step: '0.01',
            derived: true,
            derivedTitle: 'This row’s CCPS as a % of total CCPS in this tranche.'
        }
    ];
}

export function readTrancheCell(slot, part, invId) {
    if (!slot) return '';
    if (invId == null) {
        if (part === 'Sub') return slot.networkSub ?? '';
        if (part === 'Ccps') return slot.networkCcps ?? '';
        return slot.networkHolding ?? '';
    }
    const m = part === 'Sub' ? slot.invSub : part === 'Ccps' ? slot.invCcps : slot.invHolding;
    return (m && m[invId]) ?? '';
}

export function buildExtendedTranchesJsonPayload(linkFormResolved, invId, isNetwork) {
    const tc = linkTrancheCount(linkFormResolved);
    if (tc <= 3) return undefined;
    const parseOptF = (v) => (v !== undefined && String(v).trim() !== '' ? parseFloat(v) : undefined);
    const parseOptI = (v) => (v !== undefined && String(v).trim() !== '' ? parseInt(v, 10) : undefined);
    const rows = [];
    for (let slot = 3; slot < tc; slot++) {
        const slotData = linkFormResolved.trancheSlots?.[slot];
        const row = { trancheNumber: slot + 1 };
        if (isNetwork) {
            const sub = parseOptF(slotData?.networkSub);
            const nc = parseOptI(slotData?.networkCcps);
            const hp = parseOptF(slotData?.networkHolding);
            if (sub !== undefined) row.subscriptionAmount = sub;
            if (nc !== undefined) row.noCcps = nc;
            if (hp !== undefined) row.holdingPercentage = hp;
        } else {
            const sub = parseOptF(slotData?.invSub?.[invId]);
            const nc = parseOptI(slotData?.invCcps?.[invId]);
            const hp = parseOptF(slotData?.invHolding?.[invId]);
            if (sub !== undefined) row.subscriptionAmount = sub;
            if (nc !== undefined) row.noCcps = nc;
            if (hp !== undefined) row.holdingPercentage = hp;
        }
        rows.push(row);
    }
    return rows.length > 0 ? JSON.stringify(rows) : undefined;
}

export function parseLinkInvestmentNumber(v) {
    if (v === undefined || v === null || String(v).trim() === '') return NaN;
    const n = parseFloat(String(v).replace(/,/g, ''));
    return Number.isFinite(n) ? n : NaN;
}

export function formatLinkHoldingPct(p) {
    const n = typeof p === 'number' ? p : Number(p);
    if (!Number.isFinite(n)) return '';
    return (Math.round(n * 100) / 100).toFixed(2);
}

export function roundMoney2(n) {
    return Math.round(n * 100) / 100;
}

export function distributeIntByWeights(total, weights) {
    const n = weights.length;
    const out = new Array(n).fill(0);
    if (total <= 0 || n === 0) return out;
    const sumW = weights.reduce((a, b) => a + b, 0);
    if (sumW <= 0) return out;
    const floors = weights.map((w) => Math.floor((total * w) / sumW));
    let used = floors.reduce((a, b) => a + b, 0);
    let leftover = total - used;
    const order = weights.map((w, i) => ({ i, frac: (total * w) / sumW - floors[i] }));
    order.sort((a, b) => b.frac - a.frac);
    for (let k = 0; k < leftover; k++) {
        floors[order[k % n].i]++;
    }
    for (let i = 0; i < n; i++) out[i] = floors[i];
    return out;
}

export function investorGrossSumFromForm(form, investors) {
    if (!Array.isArray(investors) || investors.length === 0) return 0;
    return investors.reduce((s, inv) => {
        const a = parseLinkInvestmentNumber(form.amounts?.[inv.id]);
        return s + (Number.isFinite(a) && a > 0 ? a : 0);
    }, 0);
}

export function isEnfanceAggregateLinkMode(form, investors) {
    if (!linkUsesIssuePriceAndPartlyPaid(form.investmentType)) return false;
    const pct = parseLinkInvestmentNumber(form.partlyPaidAmountPercent);
    const pctOk = Number.isFinite(pct) && pct >= 0 && pct <= 100;
    return pctOk && investorGrossSumFromForm(form, investors) > 0;
}

export function shouldAutoFillSelfInvestment(form, investors) {
    if (!linkUsesIssuePriceAndPartlyPaid(form.investmentType)) return false;
    if (isEnfanceAggregateLinkMode(form, investors)) return true;
    const pct = parseLinkInvestmentNumber(form.partlyPaidAmountPercent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) return false;
    const totalRound = parseLinkInvestmentNumber(form.totalAmount);
    return Number.isFinite(totalRound) && totalRound > 0;
}

export function selfNetworkAmountTitle(form, investors) {
    if (isEnfanceAggregateLinkMode(form, investors)) {
        return 'Auto-filled: sum of investors’ partly paid (self investment = pooled partly paid; Enfance cap table style).';
    }
    if (shouldAutoFillSelfInvestment(form, investors)) {
        return 'Auto-filled from round total × partly paid % (no investor gross entered yet).';
    }
    return undefined;
}

export function linkModalInvestorList(allInvestors, form) {
    if (!Array.isArray(allInvestors) || allInvestors.length === 0) return [];
    const ids = form.includedInvestorIds;
    if (!Array.isArray(ids)) return allInvestors;
    if (ids.length === 0) return [];
    const set = new Set(ids);
    return allInvestors.filter((inv) => set.has(inv.id));
}

export function linkUsesIssuePriceAndPartlyPaid(investmentType) {
    const t = investmentType || 'EQUITY';
    return t === 'EQUITY' || t === 'CCPS';
}

export function linkSelfInvestmentOptional(investmentType) {
    return !linkUsesIssuePriceAndPartlyPaid(investmentType);
}

export function linkOverviewColumnsForInvestmentType(investmentType) {
    if (linkUsesIssuePriceAndPartlyPaid(investmentType)) {
        return LINK_STARTUP_SECTION_COLUMNS.overview;
    }
    return [
        {
            label: 'Committed amount',
            columnSubLabel: 'Self row optional',
            columnHint: 'Investor rows: gross committed. Network (self) row: optional — add only if the network invests alongside investors.',
            networkKey: 'networkAmount',
            stateKey: 'amounts',
            step: '1'
        }
    ];
}

export function linkTrancheCount(form) {
    const n = Number(form.trancheCount);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(TRANCHE_SLOT_MAX, Math.floor(n));
}

export function isTrancheAutoSplitActive(form) {
    const tc = linkTrancheCount(form);
    let sum = 0;
    for (let i = 0; i < tc; i++) {
        const p = parseLinkInvestmentNumber(form.trancheSplitPcts?.[i]);
        if (Number.isFinite(p) && p > 0) sum += p;
    }
    return sum > 0;
}

export function trancheSplitBaseForRow(next, invId, isNetwork) {
    if (isNetwork) {
        const primary = parseLinkInvestmentNumber(next.networkAmount);
        if (Number.isFinite(primary) && primary > 0) return primary;
        const nett = parseLinkInvestmentNumber(next.networkNettSubscription);
        if (Number.isFinite(nett) && nett > 0) return nett;
        return NaN;
    }
    const nett = parseLinkInvestmentNumber(next.nettSubscriptionAmounts?.[invId]);
    if (Number.isFinite(nett) && nett > 0) return nett;
    return parseLinkInvestmentNumber(next.amounts?.[invId]);
}

export function applyTrancheSubscriptionSplitFromPercents(next, investors) {
    if (!isTrancheAutoSplitActive(next)) return;
    const tc = linkTrancheCount(next);
    const weights = [];
    for (let i = 0; i < tc; i++) {
        weights.push(Math.max(0, parseLinkInvestmentNumber(next.trancheSplitPcts[i]) || 0));
    }
    const sumW = weights.reduce((a, b) => a + b, 0);
    if (sumW <= 0) return;

    const strAmt = (v) => (Number.isFinite(v) && v > 0 ? String(v) : '');

    const allocN = (base) => {
        if (!(base > 0)) return Array.from({ length: tc }, () => '');
        const out = [];
        let remaining = roundMoney2(base);
        for (let i = 0; i < tc; i++) {
            if (i === tc - 1) {
                out.push(strAmt(remaining));
            } else {
                const ai = roundMoney2((base * weights[i]) / sumW);
                out.push(strAmt(ai));
                remaining = roundMoney2(remaining - ai);
            }
        }
        return out;
    };

    const slots = next.trancheSlots.map((s) => ({
        ...s,
        invSub: { ...s.invSub },
        invCcps: { ...s.invCcps },
        invHolding: { ...s.invHolding }
    }));

    const partsN = allocN(trancheSplitBaseForRow(next, null, true));
    for (let i = 0; i < tc; i++) {
        slots[i].networkSub = partsN[i] || '';
    }

    investors.forEach((inv) => {
        const parts = allocN(trancheSplitBaseForRow(next, inv.id, false));
        for (let i = 0; i < tc; i++) {
            slots[i].invSub = { ...slots[i].invSub, [inv.id]: parts[i] || '' };
        }
    });

    for (let i = tc; i < TRANCHE_SLOT_MAX; i++) {
        slots[i].networkSub = '';
        const invSub = { ...slots[i].invSub };
        const invCcps = { ...slots[i].invCcps };
        const invHolding = { ...slots[i].invHolding };
        investors.forEach((inv) => {
            invSub[inv.id] = '';
            invCcps[inv.id] = '';
            invHolding[inv.id] = '';
        });
        slots[i] = { ...slots[i], invSub, invCcps, invHolding, networkCcps: '', networkHolding: '' };
    }

    next.trancheSlots = slots;
}

export function recomputeTrancheSlot(next, investors, slotIndex) {
    const slots = [...next.trancheSlots];
    const prev = slots[slotIndex] || {};
    const slot = {
        ...prev,
        invSub: { ...prev.invSub },
        invCcps: { ...prev.invCcps },
        invHolding: { ...prev.invHolding }
    };

    const priceForInv = (invId) => {
        const priceRow = parseLinkInvestmentNumber(next.shareValues?.[invId]);
        const priceGlobal = parseLinkInvestmentNumber(next.networkShareValue);
        return Number.isFinite(priceRow) && priceRow > 0 ? priceRow : priceGlobal;
    };

    const ccpsPerRow = [];
    const subsNet = parseLinkInvestmentNumber(slot.networkSub);
    const netPrice = parseLinkInvestmentNumber(next.networkShareValue);
    let nCcps = 0;
    if (Number.isFinite(subsNet) && subsNet > 0 && Number.isFinite(netPrice) && netPrice > 0) {
        nCcps = Math.round(subsNet / netPrice);
    }
    slot.networkCcps = nCcps > 0 ? String(nCcps) : '';
    ccpsPerRow.push(nCcps);

    investors.forEach((inv) => {
        const amt = parseLinkInvestmentNumber(slot.invSub[inv.id]);
        const price = priceForInv(inv.id);
        let c = 0;
        if (Number.isFinite(amt) && amt > 0 && Number.isFinite(price) && price > 0) {
            c = Math.round(amt / price);
        }
        slot.invCcps = { ...slot.invCcps, [inv.id]: c > 0 ? String(c) : '' };
        ccpsPerRow.push(c);
    });

    const totalC = ccpsPerRow.reduce((s, x) => s + x, 0);
    if (totalC > 0) {
        slot.networkHolding = formatLinkHoldingPct((100 * ccpsPerRow[0]) / totalC);
        investors.forEach((inv, i) => {
            const p = (100 * ccpsPerRow[i + 1]) / totalC;
            slot.invHolding = { ...slot.invHolding, [inv.id]: formatLinkHoldingPct(p) };
        });
    } else {
        slot.networkHolding = '';
        investors.forEach((inv) => {
            slot.invHolding = { ...slot.invHolding, [inv.id]: '' };
        });
    }

    slots[slotIndex] = slot;
    next.trancheSlots = slots;
}

export function recomputeOverviewDerived(form, investors) {
    const useSharePricing = linkUsesIssuePriceAndPartlyPaid(form.investmentType);
    const next = {
        ...form,
        amounts: { ...form.amounts },
        shareCounts: { ...form.shareCounts },
        equityPercentages: { ...form.equityPercentages },
        shareValues: { ...form.shareValues },
        partlyPaidAmounts: { ...form.partlyPaidAmounts },
        nettSubscriptionAmounts: { ...form.nettSubscriptionAmounts },
        trancheCount: linkTrancheCount(form),
        trancheSplitPcts: normalizeTrancheSplitPcts(form),
        trancheSlots: cloneTrancheSlotsFromForm(form)
    };

    if (!useSharePricing) {
        next.partlyPaidAmountPercent = '';
        next.networkShareValue = '';
        next.shareValues = {};
        const blankByInv = {};
        investors.forEach((inv) => {
            blankByInv[inv.id] = '';
        });
        next.partlyPaidAmounts = { ...blankByInv };
        next.nettSubscriptionAmounts = { ...blankByInv };
        next.networkPartlyPaid = '';
        next.networkNettSubscription = '';
    }

    const totalRound = parseLinkInvestmentNumber(next.totalAmount);
    const pctRaw = parseLinkInvestmentNumber(next.partlyPaidAmountPercent);
    const applyPartlySplit = Number.isFinite(pctRaw) && pctRaw >= 0 && pctRaw <= 100;

    const applyPartlyForAmount = (amount) => {
        if (!applyPartlySplit || !Number.isFinite(amount) || amount <= 0) return { partly: null, nett: null };
        const partly = roundMoney2(amount * (pctRaw / 100));
        const nett = roundMoney2(amount - partly);
        return { partly, nett };
    };

    let sumPartlyInv = 0;
    let sumGrossInv = 0;

    investors.forEach((inv) => {
        const amt = parseLinkInvestmentNumber(next.amounts[inv.id]);
        if (Number.isFinite(amt) && amt > 0) sumGrossInv += amt;
        const invSplit = applyPartlyForAmount(amt);
        if (applyPartlySplit) {
            if (invSplit.partly != null) {
                sumPartlyInv += invSplit.partly;
                next.partlyPaidAmounts = { ...next.partlyPaidAmounts, [inv.id]: String(invSplit.partly) };
                next.nettSubscriptionAmounts = { ...next.nettSubscriptionAmounts, [inv.id]: String(invSplit.nett) };
            } else {
                next.partlyPaidAmounts = { ...next.partlyPaidAmounts, [inv.id]: '' };
                next.nettSubscriptionAmounts = { ...next.nettSubscriptionAmounts, [inv.id]: '' };
            }
        }
    });

    if (applyPartlySplit && sumGrossInv > 0) {
        next.networkPartlyPaid = String(roundMoney2(sumPartlyInv));
        // Pool nett = total gross − total partly (one subtraction); avoids drift vs summing per-investor rounded netts.
        next.networkNettSubscription = String(roundMoney2(sumGrossInv - sumPartlyInv));
        next.networkAmount = String(roundMoney2(sumPartlyInv));
    } else if (applyPartlySplit && Number.isFinite(totalRound) && totalRound > 0) {
        const partly = roundMoney2((totalRound * pctRaw) / 100);
        next.networkAmount = String(partly);
        next.networkPartlyPaid = String(partly);
        next.networkNettSubscription = String(roundMoney2(totalRound - partly));
    } else {
        next.networkPartlyPaid = '';
        next.networkNettSubscription = '';
    }

    const netAmt = parseLinkInvestmentNumber(next.networkAmount);
    const netPrice = parseLinkInvestmentNumber(next.networkShareValue);

    const enfanceCcpsPool = applyPartlySplit && sumGrossInv > 0;

    const naiveInvCcps = investors.map((inv) => {
        const amt = parseLinkInvestmentNumber(next.amounts[inv.id]);
        const priceRow = parseLinkInvestmentNumber(next.shareValues[inv.id]);
        const priceGlobal = parseLinkInvestmentNumber(next.networkShareValue);
        const price = Number.isFinite(priceRow) && priceRow > 0 ? priceRow : priceGlobal;
        if (Number.isFinite(amt) && amt > 0 && Number.isFinite(price) && price > 0) {
            return Math.round(amt / price);
        }
        return 0;
    });
    const poolTotalCcps = naiveInvCcps.reduce((a, b) => a + b, 0);

    const ccpsPerRow = [];

    let netCcps = 0;
    if (enfanceCcpsPool && poolTotalCcps > 0) {
        if (Number.isFinite(netAmt) && netAmt > 0 && Number.isFinite(netPrice) && netPrice > 0) {
            netCcps = Math.min(Math.round(netAmt / netPrice), poolTotalCcps);
        } else {
            netCcps = 0;
        }
        next.networkShareCount = netCcps > 0 ? String(netCcps) : '';

        const remaining = Math.max(0, poolTotalCcps - netCcps);
        const weights = naiveInvCcps.map((nv, i) => {
            if (nv > 0) return nv;
            const g = parseLinkInvestmentNumber(next.amounts[investors[i].id]);
            return Number.isFinite(g) && g > 0 ? g : 0;
        });
        const dist = distributeIntByWeights(remaining, weights);
        investors.forEach((inv, i) => {
            const c = dist[i] ?? 0;
            next.shareCounts = { ...next.shareCounts, [inv.id]: c > 0 ? String(c) : '' };
        });
        ccpsPerRow.push(netCcps);
        investors.forEach((inv, i) => {
            ccpsPerRow.push(dist[i] ?? 0);
        });
    } else {
        if (Number.isFinite(netAmt) && netAmt > 0 && Number.isFinite(netPrice) && netPrice > 0) {
            netCcps = Math.round(netAmt / netPrice);
            next.networkShareCount = String(netCcps);
        } else if (Number.isFinite(netAmt) && netAmt <= 0) {
            next.networkShareCount = '';
            netCcps = 0;
        } else {
            const stored = parseInt(String(next.networkShareCount ?? '').trim(), 10);
            netCcps = Number.isFinite(stored) && stored > 0 ? stored : 0;
        }
        ccpsPerRow.push(netCcps);

        investors.forEach((inv) => {
            const amt = parseLinkInvestmentNumber(next.amounts[inv.id]);
            const priceRow = parseLinkInvestmentNumber(next.shareValues[inv.id]);
            const priceGlobal = parseLinkInvestmentNumber(next.networkShareValue);
            const price = Number.isFinite(priceRow) && priceRow > 0 ? priceRow : priceGlobal;
            let ccps = 0;
            if (Number.isFinite(amt) && amt > 0 && Number.isFinite(price) && price > 0) {
                ccps = Math.round(amt / price);
                next.shareCounts = { ...next.shareCounts, [inv.id]: String(ccps) };
            } else if (Number.isFinite(amt) && amt <= 0) {
                next.shareCounts = { ...next.shareCounts, [inv.id]: '' };
                ccps = 0;
            } else {
                const stored = parseInt(String(next.shareCounts[inv.id] ?? '').trim(), 10);
                ccps = Number.isFinite(stored) && stored > 0 ? stored : 0;
            }
            ccpsPerRow.push(ccps);
        });
    }

    const totalCcps = ccpsPerRow.reduce((s, c) => s + c, 0);
    if (totalCcps > 0) {
        next.networkEquityPercentage = formatLinkHoldingPct((100 * ccpsPerRow[0]) / totalCcps);
        investors.forEach((inv, i) => {
            const pct = (100 * ccpsPerRow[i + 1]) / totalCcps;
            next.equityPercentages = { ...next.equityPercentages, [inv.id]: formatLinkHoldingPct(pct) };
        });
    } else {
        next.networkEquityPercentage = '';
        investors.forEach((inv) => {
            next.equityPercentages = { ...next.equityPercentages, [inv.id]: '' };
        });
    }

    applyTrancheSubscriptionSplitFromPercents(next, investors);

    const tc = linkTrancheCount(next);
    for (let i = 0; i < tc; i++) {
        recomputeTrancheSlot(next, investors, i);
    }

    return next;
}

/** Pure reducer: patch one cap-table cell (overview or tranche tab). */
export function patchLinkStartupInvestmentFormState(f, column, kind, invId, rawValue) {
    if (column.placeholder || column.derived) return f;
    if (column.trancheSlotIndex != null && column.tranchePart) {
        const si = column.trancheSlotIndex;
        const part = column.tranchePart;
        const slots = cloneTrancheSlotsFromForm(f);
        const cur = slots[si] || createEmptyTrancheSlots()[0];
        const clearTrancheSplit = part === 'Sub' && isTrancheAutoSplitActive(f);
        if (kind === 'network') {
            if (part === 'Sub') slots[si] = { ...cur, networkSub: rawValue };
            else if (part === 'Ccps') slots[si] = { ...cur, networkCcps: rawValue };
            else slots[si] = { ...cur, networkHolding: rawValue };
        } else {
            const key = part === 'Sub' ? 'invSub' : part === 'Ccps' ? 'invCcps' : 'invHolding';
            slots[si] = { ...cur, [key]: { ...(cur[key] || {}), [invId]: rawValue } };
        }
        if (clearTrancheSplit) {
            return {
                ...f,
                trancheSlots: slots,
                trancheSplitPcts: Array.from({ length: TRANCHE_SLOT_MAX }, () => '0')
            };
        }
        return { ...f, trancheSlots: slots };
    }
    if (kind === 'network') {
        return { ...f, [column.networkKey]: rawValue };
    }
    const bucket = f[column.stateKey] || {};
    return { ...f, [column.stateKey]: { ...bucket, [invId]: rawValue } };
}

/** One issue price for the round: network row + all investors in the deal (modal list). */
export function applyIssuePriceToAllRowsState(f, raw, allInvestors) {
    const shareValues = { ...f.shareValues };
    linkModalInvestorList(allInvestors, f).forEach((inv) => {
        shareValues[inv.id] = raw;
    });
    return { ...f, networkShareValue: raw, shareValues };
}

export function createInitialLinkInvestmentsForm(options = {}) {
    const { investors = [], networkPartlyPaidPctSetting = '', investmentStage = 'SEED', trancheCount = 2 } = options;
    return {
        notReadyToInvestYet: false,
        investmentStage,
        currency: 'INR',
        investmentType: 'EQUITY',
        totalAmount: '',
        networkAmount: '',
        networkEquityPercentage: '',
        networkShareValue: '',
        networkShareCount: '',
        networkEntryShareEquity: '',
        networkPartlyPaid: '',
        networkNettSubscription: '',
        shareValues: {},
        shareCounts: {},
        equityPercentages: {},
        amounts: {},
        entryShareEquities: {},
        partlyPaidAmounts: {},
        nettSubscriptionAmounts: {},
        trancheCount,
        trancheSplitPcts: defaultTrancheSplitPcts(),
        trancheSlots: createEmptyTrancheSlots(),
        partlyPaidAmountPercent: String(networkPartlyPaidPctSetting || '').trim() !== '' ? String(networkPartlyPaidPctSetting) : '',
        includedInvestorIds: Array.isArray(investors) && investors.length > 0 ? investors.map((i) => i.id) : null
    };
}

/**
 * Prefill link-style cap table from existing startup `investment` rows (portfolio ledger).
 * Returns form state + investor list for {@link NetworkLinkStyleCapTableEditor}.
 */
export function hydrateLinkFormFromInvestmentsList(investmentRows, networkUserId, options = {}) {
    const { networkPartlyPaidPctSetting = '' } = options;
    const rows = Array.isArray(investmentRows) ? investmentRows : [];
    const nuid = networkUserId != null && networkUserId !== '' ? Number(networkUserId) : NaN;
    const hasNetworkId = Number.isFinite(nuid);

    const networkRows = hasNetworkId ? rows.filter((r) => Number(r?.investorId) === nuid) : [];
    const memberRows = hasNetworkId ? rows.filter((r) => Number(r?.investorId) !== nuid) : [...rows];

    const seen = new Set();
    const editorInvestors = [];
    memberRows.forEach((r) => {
        if (r?.investorId == null || seen.has(r.investorId)) return;
        seen.add(r.investorId);
        editorInvestors.push({
            id: r.investorId,
            name: r.investorName || '',
            email: r.investorEmail || ''
        });
    });

    const first = rows[0] || {};
    const totalSum = rows.reduce((s, r) => s + (parseFloat(String(r?.investmentAmount ?? '').replace(/,/g, '')) || 0), 0);

    let trancheCount = 2;
    const hasPost = rows.some((r) => {
        const a = String(r?.postCompletionSubscriptionAmount ?? '').trim();
        const b = String(r?.postCompletionNoCcps ?? '').trim();
        return a !== '' || b !== '';
    });
    if (hasPost) trancheCount = 3;

    const cellStr = (v) => {
        if (v === undefined || v === null || v === '') return '';
        return String(v).replace(/,/g, '');
    };

    const slots = createEmptyTrancheSlots();
    const writeInvTranche = (r, slotIdx, subKey, ccpsKey, holdKey) => {
        const iid = r.investorId;
        if (iid == null) return;
        const cur = slots[slotIdx] || createEmptyTrancheSlots()[0];
        const invSub = { ...cur.invSub };
        const invCcps = { ...cur.invCcps };
        const invHolding = { ...cur.invHolding };
        const s = cellStr(r[subKey]);
        const c = cellStr(r[ccpsKey]);
        const h = cellStr(r[holdKey]);
        if (s !== '') invSub[iid] = s;
        if (c !== '') invCcps[iid] = c;
        if (h !== '') invHolding[iid] = h;
        slots[slotIdx] = { ...cur, invSub, invCcps, invHolding };
    };
    const writeNetTranche = (r, slotIdx, subKey, ccpsKey, holdKey) => {
        const cur = slots[slotIdx] || createEmptyTrancheSlots()[0];
        const sub = cellStr(r[subKey]);
        const ccps = cellStr(r[ccpsKey]);
        const hold = cellStr(r[holdKey]);
        const next = { ...cur };
        if (sub !== '') next.networkSub = sub;
        if (ccps !== '') next.networkCcps = ccps;
        if (hold !== '') next.networkHolding = hold;
        slots[slotIdx] = next;
    };

    memberRows.forEach((r) => {
        writeInvTranche(r, 0, 'tranche1SubscriptionAmount', 'tranche1NoCcps', 'tranche1HoldingPercentage');
        writeInvTranche(r, 1, 'tranche2SubscriptionAmount', 'tranche2NoCcps', 'tranche2HoldingPercentage');
        writeInvTranche(r, 2, 'postCompletionSubscriptionAmount', 'postCompletionNoCcps', 'postCompletionHoldingPercentage');
    });
    networkRows.forEach((r) => {
        writeNetTranche(r, 0, 'tranche1SubscriptionAmount', 'tranche1NoCcps', 'tranche1HoldingPercentage');
        writeNetTranche(r, 1, 'tranche2SubscriptionAmount', 'tranche2NoCcps', 'tranche2HoldingPercentage');
        writeNetTranche(r, 2, 'postCompletionSubscriptionAmount', 'postCompletionNoCcps', 'postCompletionHoldingPercentage');
    });

    const amounts = {};
    const shareValues = {};
    const equityPercentages = {};
    const shareCounts = {};
    const entryShareEquities = {};
    const partlyPaidAmounts = {};
    const nettSubscriptionAmounts = {};

    memberRows.forEach((r) => {
        const iid = r.investorId;
        amounts[iid] = cellStr(r.investmentAmount);
        shareValues[iid] = cellStr(r.entrySharePrice);
        equityPercentages[iid] = cellStr(r.equityPercentage);
        shareCounts[iid] = cellStr(r.numberOfShares);
        entryShareEquities[iid] = cellStr(r.entryShareEquity);
        partlyPaidAmounts[iid] = cellStr(r.partlyPaidAmount);
        nettSubscriptionAmounts[iid] = cellStr(r.nettSubscriptionAmount);
    });

    const nr = networkRows[0];
    let networkAmount = '';
    let networkShareValue = '';
    let networkShareCount = '';
    let networkEquityPercentage = '';
    let networkEntryShareEquity = '';
    let networkPartlyPaid = '';
    let networkNettSubscription = '';
    if (nr) {
        networkAmount = cellStr(nr.investmentAmount);
        networkShareValue = cellStr(nr.entrySharePrice);
        networkShareCount = cellStr(nr.numberOfShares);
        networkEquityPercentage = cellStr(nr.equityPercentage);
        networkEntryShareEquity = cellStr(nr.entryShareEquity);
        networkPartlyPaid = cellStr(nr.partlyPaidAmount);
        networkNettSubscription = cellStr(nr.nettSubscriptionAmount);
    }

    const base = createInitialLinkInvestmentsForm({
        investors: editorInvestors,
        networkPartlyPaidPctSetting,
        investmentStage: first.investmentStage || 'SEED',
        trancheCount
    });

    let form = {
        ...base,
        currency: first.currency || 'INR',
        investmentType: first.investmentType || 'EQUITY',
        investmentStage: first.investmentStage || 'SEED',
        totalAmount: totalSum > 0 ? String(totalSum) : '',
        amounts,
        shareValues,
        equityPercentages,
        shareCounts,
        entryShareEquities,
        partlyPaidAmounts,
        nettSubscriptionAmounts,
        networkAmount,
        networkShareValue,
        networkShareCount,
        networkEquityPercentage,
        networkEntryShareEquity,
        networkPartlyPaid,
        networkNettSubscription,
        trancheSlots: slots,
        trancheCount,
        includedInvestorIds: editorInvestors.map((i) => i.id)
    };

    if (options.capTableLinkSettings) {
        form = mergeCapTableLinkSettingsIntoForm(form, options.capTableLinkSettings);
    } else {
        form = {
            ...form,
            trancheSplitPcts: inferTrancheSplitPctsFromInvestmentRows(rows, trancheCount)
        };
    }

    form = recomputeOverviewDerived(form, editorInvestors);

    return { form, editorInvestors };
}

/** Flat body for PATCH /investments/{id}/amount from one `buildLinkInvestmentsPayload` row. */
export function patchBodyFromLinkBuiltInvestmentRow(row) {
    if (!row || row.amount == null || Number.isNaN(Number(row.amount))) return null;
    const body = { amount: Number(row.amount) };
    const keys = [
        'entrySharePrice',
        'equityPercentage',
        'numberOfShares',
        'entryShareEquity',
        'partlyPaidAmount',
        'nettSubscriptionAmount',
        'tranche1SubscriptionAmount',
        'tranche1NoCcps',
        'tranche1HoldingPercentage',
        'tranche2SubscriptionAmount',
        'tranche2NoCcps',
        'tranche2HoldingPercentage',
        'postCompletionSubscriptionAmount',
        'postCompletionNoCcps',
        'postCompletionHoldingPercentage'
    ];
    keys.forEach((k) => {
        if (row[k] !== undefined && row[k] !== null) body[k] = row[k];
    });
    if (row.extendedTranchesJson !== undefined && row.extendedTranchesJson !== null && String(row.extendedTranchesJson).trim() !== '') {
        body.extendedTranchesJson = row.extendedTranchesJson;
    }
    return body;
}

/** Map `investorId` → investment row `id` for PATCH (last row wins if duplicates). */
export function investmentIdByInvestorId(investmentRows) {
    const m = new Map();
    (Array.isArray(investmentRows) ? investmentRows : []).forEach((r) => {
        if (r?.investorId != null && r?.id != null) m.set(Number(r.investorId), r.id);
    });
    return m;
}

/**
 * Build `investments` array + allocation totals for `link-startup-with-investments` API.
 */
export function buildLinkInvestmentsPayload(linkInvestmentsForm, investors, effectiveNetworkId) {
    const modalInv = linkModalInvestorList(investors, linkInvestmentsForm);
    const linkFormResolved = recomputeOverviewDerived({ ...linkInvestmentsForm }, modalInv);
    const parseOptF = (v) => (v !== undefined && String(v).trim() !== '' ? parseFloat(v) : undefined);
    const parseOptI = (v) => (v !== undefined && String(v).trim() !== '' ? parseInt(v, 10) : undefined);
    const entrySharePriceFor = (invId) =>
        parseOptF(linkFormResolved.shareValues[invId]) ?? parseOptF(linkFormResolved.networkShareValue);
    const investments = modalInv
        .map((inv) => ({
            investorId: inv.id,
            amount: parseFloat(linkFormResolved.amounts[inv.id]) || 0,
            equityPercentage: parseOptF(linkFormResolved.equityPercentages[inv.id]),
            entrySharePrice: entrySharePriceFor(inv.id),
            entryShareEquity: parseOptF(linkFormResolved.entryShareEquities[inv.id]),
            numberOfShares: parseOptI(linkFormResolved.shareCounts[inv.id]),
            partlyPaidAmount: parseOptF(linkFormResolved.partlyPaidAmounts[inv.id]),
            nettSubscriptionAmount: parseOptF(linkFormResolved.nettSubscriptionAmounts[inv.id]),
            tranche1SubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[0]?.invSub?.[inv.id]),
            tranche1NoCcps: parseOptI(linkFormResolved.trancheSlots?.[0]?.invCcps?.[inv.id]),
            tranche1HoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[0]?.invHolding?.[inv.id]),
            tranche2SubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[1]?.invSub?.[inv.id]),
            tranche2NoCcps: parseOptI(linkFormResolved.trancheSlots?.[1]?.invCcps?.[inv.id]),
            tranche2HoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[1]?.invHolding?.[inv.id]),
            postCompletionSubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[2]?.invSub?.[inv.id]),
            postCompletionNoCcps: parseOptI(linkFormResolved.trancheSlots?.[2]?.invCcps?.[inv.id]),
            postCompletionHoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[2]?.invHolding?.[inv.id]),
            extendedTranchesJson: buildExtendedTranchesJsonPayload(linkFormResolved, inv.id, false)
        }))
        .filter((e) => e.amount > 0);
    const networkAmount = parseFloat(linkFormResolved.networkAmount) || 0;
    if (networkAmount > 0 && effectiveNetworkId) {
        investments.unshift({
            investorId: effectiveNetworkId,
            amount: networkAmount,
            equityPercentage: parseOptF(linkFormResolved.networkEquityPercentage),
            entrySharePrice: parseOptF(linkFormResolved.networkShareValue),
            entryShareEquity: parseOptF(linkFormResolved.networkEntryShareEquity),
            numberOfShares: parseOptI(linkFormResolved.networkShareCount),
            partlyPaidAmount: parseOptF(linkFormResolved.networkPartlyPaid),
            nettSubscriptionAmount: parseOptF(linkFormResolved.networkNettSubscription),
            tranche1SubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[0]?.networkSub),
            tranche1NoCcps: parseOptI(linkFormResolved.trancheSlots?.[0]?.networkCcps),
            tranche1HoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[0]?.networkHolding),
            tranche2SubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[1]?.networkSub),
            tranche2NoCcps: parseOptI(linkFormResolved.trancheSlots?.[1]?.networkCcps),
            tranche2HoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[1]?.networkHolding),
            postCompletionSubscriptionAmount: parseOptF(linkFormResolved.trancheSlots?.[2]?.networkSub),
            postCompletionNoCcps: parseOptI(linkFormResolved.trancheSlots?.[2]?.networkCcps),
            postCompletionHoldingPercentage: parseOptF(linkFormResolved.trancheSlots?.[2]?.networkHolding),
            extendedTranchesJson: buildExtendedTranchesJsonPayload(linkFormResolved, null, true)
        });
    }
    const totalAmount = parseFloat(linkInvestmentsForm.totalAmount);
    const enfanceAggregate = isEnfanceAggregateLinkMode(linkFormResolved, modalInv);
    const investorGrossTotal = investorGrossSumFromForm(linkFormResolved, modalInv);
    const allocatedAmount = enfanceAggregate ? investorGrossTotal : investments.reduce((sum, entry) => sum + entry.amount, 0);
    return {
        investments,
        totalAmount,
        linkFormResolved,
        modalInv,
        allocatedAmount,
        enfanceAggregate,
        investorGrossTotal,
        capTableLinkSettings: extractCapTableLinkSettingsFromForm(linkInvestmentsForm)
    };
}
